// Generated from api/openapi/agents.yaml by make docs. Do not edit.
import { z } from "zod";
import { consequencePolicySchema, dimMetaSchema, principalRefSchema } from "@pureid/auth-core";

export const agentSchema = z.object({
  "id": z.string().uuid(),
  "tenant_id": z.string().uuid(),
  "organization_id": z.string().uuid().optional(),
  "edge_group_id": z.string().uuid().nullable(),
  "kind": z.enum(["agent", "workload_identity", "api_key", "self"]),
  "name": z.string(),
  "owner_user_id": z.string().uuid().optional(),
  "owner_admin_id": z.string().uuid().optional(),
  "backup_owner_user_id": z.string().uuid().optional(),
  "backup_owner_admin_id": z.string().uuid().optional(),
  "owner": principalRefSchema.optional(),
  "backup_owner": principalRefSchema.optional(),
  "purpose": z.string(),
  "risk_tier": z.enum(["read_only", "low", "medium", "high"]),
  "autonomy_tier": z.enum(["supervised", "bounded", "autonomous"]),
  "max_delegation_depth": z.number().int(),
  "max_scope": z.array(z.string()),
  "allowed_data_class": z.enum(["public", "internal", "confidential", "restricted"]),
  "allowed_egress": z.array(z.string()),
  "min_attestation": z.enum(["token_only", "cloud_instance", "vtpm_measured"]),
  "bearer_presentation": z.boolean(),
  "allowed_resources": z.array(z.string()),
  "max_txn_count": z.number().int().optional(),
  "consequence_policy": consequencePolicySchema.optional(),
  "dimensions": z.record(z.string(), dimMetaSchema).optional(),
  "delegable_by_user_ids": z.array(z.string().uuid()),
  "cnf_pinned": z.boolean(),
  "cnf_thumbprint": z.string().optional(),
  "cnf_method": z.enum(["mtls", "dpop"]).optional(),
  "workload_bound": z.boolean(),
  "fed_issuer": z.string().optional(),
  "fed_subject": z.string().optional(),
  "lifecycle_state": z.enum(["created", "certified", "active", "suspended", "quarantined", "retired"]),
  "review_due_at": z.string().datetime({ offset: true }).optional(),
  "created_at": z.string().datetime({ offset: true }),
  "updated_at": z.string().datetime({ offset: true })
}).strict();

export const agentsListResponseSchema = z.object({
  "data": z.array(agentSchema),
  "pagination": z.object({
    "total": z.number().int().min(0),
    "offset": z.number().int().min(0),
    "limit": z.number().int().min(1).max(200)
  }).strict()
}).strict();

export const ceilingChangeSchema = z.object({
  "field": z.enum(["risk_tier", "autonomy_tier", "allowed_data_class", "min_attestation", "max_delegation_depth", "max_scope", "allowed_egress", "allowed_resources", "max_txn_count", "consequence_policy"]),
  "from": z.string(),
  "to": z.string()
}).strict();

export const recertifyAgentResponseSchema = z.object({
  "agent": agentSchema,
  "changes": z.array(ceilingChangeSchema).min(1)
}).strict();

export const simulateDimensionSchema = z.object({
  "dimension": z.string(),
  "quantity": z.number().int(),
  "band": z.enum(["autonomous", "requires_approval", "denied"]),
  "reason": z.enum(["within_autonomous", "over_per_action_max", "per_action_hard_ceiling", "unsized_dimension", "quantity_overflow"]),
  "effective_per_action_max": z.number().int().optional(),
  "effective_per_action_hard_max": z.number().int().optional(),
  "binding_layer": z.enum(["agent", "tenant_default", "equal"]).optional(),
  "effective_cumulative_max": z.number().int().optional(),
  "cumulative_breached": z.boolean().optional()
}).strict();

export const simulatePolicyResponseSchema = z.object({
  "outcome": z.enum(["autonomous", "requires_approval", "denied"]),
  "reasons": z.array(z.enum(["agent_not_active", "tenant_thresholds_off", "risk_tier_high", "over_per_action_max", "per_action_hard_ceiling", "unsized_dimension", "quantity_overflow", "qualified_rules_present", "cumulative_hard_deny", "cumulative_breach", "cumulative_fail_open_alarm"])),
  "risk_tier_forces_approval": z.boolean(),
  "tenant_thresholds_configured": z.boolean(),
  "agent_lifecycle_state": z.enum(["created", "certified", "active", "suspended", "quarantined", "retired"]),
  "used_draft_policy": z.boolean(),
  "qualified_rules_present": z.boolean().optional(),
  "dimensions": z.array(simulateDimensionSchema),
  "cumulative_not_evaluated": z.boolean(),
  "dimensions_meta": z.record(z.string(), dimMetaSchema).optional()
}).strict();

export const agentEnrollmentProvenanceSchema = z.object({
  "enrolled": z.literal(true),
  "fingerprint": z.string(),
  "cnf_method": z.enum(["dpop"]),
  "owner_user_id": z.string().uuid(),
  "agent_label": z.string().optional(),
  "paired_at": z.string().datetime({ offset: true }).optional()
}).strict();

export const dimensionCatalogEntrySchema = z.object({
  "key": z.string(),
  "display_name": z.string(),
  "exponent": z.number().int(),
  "aggregation": z.enum(["sum", "count", "max", "unique_count"]),
  "unit_code": z.string().optional()
}).strict();

export const dimensionCatalogResponseSchema = z.object({
  "dimensions": z.array(dimensionCatalogEntrySchema)
}).strict();

export const budgetChainRollupRowSchema = z.object({
  "dimension_key": z.string(),
  "total": z.number().int(),
  "self": z.number().int(),
  "carried": z.number().int()
}).strict();

export const budgetChainRollupSchema = z.object({
  "agent_id": z.string().uuid(),
  "chain_depth": z.number().int(),
  "chain_ids": z.array(z.string().uuid()),
  "rows": z.array(budgetChainRollupRowSchema)
}).strict();

export const stopPreviewSchema = z.object({
  "agent_id": z.string().uuid(),
  "consents": z.number().int(),
  "human_ids": z.array(z.string().uuid()),
  "active_standings": z.number().int(),
  "live_grants": z.number().int(),
  "last_grant_expiry": z.string().datetime({ offset: true }).optional()
}).strict();

export const agentRevokeResponseSchema = z.object({
  "agent_id": z.string().uuid(),
  "revoked": z.boolean(),
  "reason": z.enum(["agent_kill_switch"])
}).strict();

export const killPrincipalResponseSchema = z.object({
  "agent_id": z.string().uuid(),
  "killed": z.boolean(),
  "suspended": z.boolean(),
  "standing_revoked": z.number().int(),
  "standing_revoke_failed": z.boolean(),
  "partial": z.boolean(),
  "reason": z.enum(["agent_kill_switch"])
}).strict();

export const pausePrincipalResponseSchema = z.object({
  "agent_id": z.string().uuid(),
  "suspended": z.boolean(),
  "standings_paused": z.number().int()
}).strict();

export const resumePrincipalResponseSchema = z.object({
  "agent_id": z.string().uuid(),
  "activated": z.boolean(),
  "standings_resumed": z.number().int(),
  "standings_conflicted": z.number().int(),
  "standings_left_paused": z.number().int()
}).strict();

export const evidenceDecisionSchema = z.object({
  "seq": z.number().int(),
  "decision": z.enum(["permit", "step_up", "deny", "quarantine"]),
  "reason_codes": z.array(z.string()),
  "call_scope": z.string().optional(),
  "call_consequence": z.object({
    "measurements": z.array(z.object({
      "dimension": z.string(),
      "quantity": z.number().int()
    }).strict()).optional()
  }).strict(),
  "occurred_at": z.string().datetime({ offset: true }),
  "edge_kid": z.string(),
  "control_plane_kid": z.string(),
  "co_signed": z.boolean(),
  "signatures_valid": z.boolean(),
  "format_unrecognized": z.boolean().optional()
}).strict();

export const grantEvidenceSchema = z.object({
  "grant": z.object({
    "jti": z.string().uuid(),
    "agent_id": z.string().uuid(),
    "human_sub": z.string().uuid(),
    "resource": z.string(),
    "scope": z.array(z.string()),
    "data_class": z.string(),
    "risk_tier": z.string(),
    "attestation_tier": z.string(),
    "revoked": z.boolean(),
    "budget_txn_max": z.number().int().optional(),
    "budget_txn_used": z.number().int()
  }).strict(),
  "rows_verified": z.boolean(),
  "all_co_signed": z.boolean(),
  "all_signatures_valid": z.boolean(),
  "unrecognized_formats": z.number().int().optional(),
  "decisions": z.array(evidenceDecisionSchema)
}).strict();

export const chainVerifySchema = z.object({
  "ok": z.boolean(),
  "checked": z.number().int(),
  "fail_seq": z.number().int().optional(),
  "reason": z.string().optional(),
  "ordering_attested": z.boolean(),
  "attested_through_seq": z.number().int(),
  "ordering_note": z.string().optional()
}).strict();

export const grantLifecycleSchema = z.object({
  "jti": z.string().uuid(),
  "agent_id": z.string().uuid(),
  "agent_name": z.string().optional(),
  "human_sub": z.string().uuid(),
  "human_name": z.string().optional(),
  "human": principalRefSchema.optional(),
  "resource": z.string(),
  "scope": z.array(z.string()),
  "data_class": z.string(),
  "risk_tier": z.string(),
  "attestation_tier": z.string(),
  "budget_txn_max": z.number().int().optional(),
  "budget_txn_used": z.number().int(),
  "created_at": z.string().datetime({ offset: true }),
  "expires_at": z.string().datetime({ offset: true }),
  "status": z.enum(["active", "expired", "revoked"]),
  "reason": z.string().optional()
}).strict();

export const grantsListResponseSchema = z.object({
  "grants": z.array(grantLifecycleSchema),
  "total": z.number().int()
}).strict();

export const resourceCustodySchema = z.object({
  "resource": z.string(),
  "placed": z.boolean(),
  "custody_mode": z.enum(["copy_in", "referenced", "edge_held"]).optional(),
  "scheme": z.string().regex(new RegExp("^[A-Za-z][A-Za-z0-9-]{0,31}$")).optional(),
  "placed_at": z.string().datetime({ offset: true }).optional(),
  "rotated_at": z.string().datetime({ offset: true }).optional(),
  "last_released_at": z.string().datetime({ offset: true }).optional(),
  "releases": z.number().int().min(0)
}).strict();

export const standingUtilizationSchema = z.object({
  "dimension": z.string(),
  "display_name": z.string(),
  "used": z.number().int(),
  "held": z.number().int(),
  "max": z.number().int(),
  "exponent": z.number().int(),
  "unit_code": z.string().optional(),
  "window_kind": z.enum(["delegation_lifetime", "rolling", "calendar_day", "calendar_month"]).optional()
}).strict();

export const standingDelegationSchema = z.object({
  "id": z.string().uuid(),
  "agent_id": z.string().uuid(),
  "agent_name": z.string().optional(),
  "human_sub": z.string().uuid(),
  "human": principalRefSchema.optional(),
  "resource": z.string(),
  "audience": z.string(),
  "scope": z.array(z.string()),
  "egress": z.array(z.string()),
  "data_class": z.enum(["public", "internal", "confidential", "restricted"]),
  "purpose": z.string(),
  "txn_count_max": z.number().int().optional(),
  "frozen_consequence_policy": consequencePolicySchema.optional(),
  "status": z.enum(["active", "paused", "revoked", "expired"]),
  "uses": z.number().int(),
  "max_uses": z.number().int().optional(),
  "not_after": z.string().datetime({ offset: true }),
  "last_minted_at": z.string().datetime({ offset: true }).optional(),
  "created_at": z.string().datetime({ offset: true }),
  "revoked_reason": z.string().optional(),
  "kind": z.enum(["short_term", "ongoing"]),
  "initiator": z.enum(["human", "agent"]),
  "consent_origin": z.enum(["admin_console", "short_term", "agent_requested"]).optional(),
  "human_is_admin": z.boolean(),
  "legacy_no_user_consent": z.boolean(),
  "has_active_user_consent": z.boolean(),
  "start_by": z.string().datetime({ offset: true }).optional(),
  "activated_at": z.string().datetime({ offset: true }).optional(),
  "active_until": z.string().datetime({ offset: true }).optional(),
  "lifetime_utilization": z.array(standingUtilizationSchema).optional(),
  "dimensions": z.record(z.string(), dimMetaSchema).optional()
}).strict();

export const createStandingDelegationResponseSchema = z.object({
  "id": z.string().uuid(),
  "not_after": z.string().datetime({ offset: true }),
  "expires_in": z.number().int()
}).strict();

export const budgetMemberSchema = z.object({
  "agent_id": z.string().uuid(),
  "agent_name": z.string().optional(),
  "used": z.number().int()
}).strict();

export const budgetPoolSchema = z.object({
  "dimension": z.string(),
  "display_name": z.string(),
  "unit_code": z.string().optional(),
  "exponent": z.number().int(),
  "max": z.number().int(),
  "used": z.number().int(),
  "held": z.number().int(),
  "remaining": z.number().int(),
  "members": z.array(budgetMemberSchema)
}).strict();

export const budgetBreakdownSchema = z.object({
  "standing_id": z.string().uuid(),
  "pools": z.array(budgetPoolSchema)
}).strict();

export const crewHumanSchema = z.object({
  "sub": z.string().uuid(),
  "name": z.string().optional()
}).strict();

export const crewMemberSchema = z.object({
  "agent_id": z.string().uuid(),
  "name": z.string().optional(),
  "parent_agent_id": z.string().uuid().optional(),
  "role": z.enum(["Spends from the shared budget", "Acts, no budget drawn", "Actions held or refused", "No activity yet"]),
  "live": z.boolean()
}).strict();

export const crewDrawSchema = z.object({
  "dimension": z.string(),
  "quantity": z.number().int()
}).strict();

export const crewStepSchema = z.object({
  "seq": z.number().int(),
  "agent_id": z.string().uuid(),
  "decision": z.enum(["permitted", "escalated", "blocked"]),
  "occurred_at": z.string().datetime({ offset: true }),
  "draws": z.array(crewDrawSchema)
}).strict();

export const crewPoolMemberSchema = z.object({
  "agent_id": z.string().uuid(),
  "used": z.number().int()
}).strict();

export const crewPoolSchema = z.object({
  "dimension": z.string(),
  "display_name": z.string(),
  "unit_code": z.string().optional(),
  "exponent": z.number().int(),
  "max": z.number().int(),
  "used": z.number().int(),
  "held": z.number().int(),
  "remaining": z.number().int(),
  "other_used": z.number().int(),
  "members": z.array(crewPoolMemberSchema)
}).strict();

export const crewViewSchema = z.object({
  "standing_id": z.string().uuid(),
  "truncated": z.boolean(),
  "human": crewHumanSchema,
  "members": z.array(crewMemberSchema),
  "budgets": z.array(crewPoolSchema),
  "steps": z.array(crewStepSchema),
  "dimensions": z.record(z.string(), dimMetaSchema)
}).strict();

export const budgetRaiseSchema = z.object({
  "id": z.string().uuid(),
  "scope_kind": z.enum(["agent", "delegation", "standing_delegation", "actor_human", "tenant"]),
  "agent_id": z.string().uuid().optional(),
  "agent_name": z.string().optional(),
  "dimension": z.string(),
  "amount": z.number().int().min(1),
  "status": z.enum(["proposed", "active", "superseded", "rejected", "expired", "lapsed"]),
  "justification": z.string(),
  "window_kind": z.enum(["delegation_lifetime", "rolling", "calendar_day", "calendar_month"]),
  "window_seconds": z.number().int().optional(),
  "proposer_admin_id": z.string().uuid(),
  "proposer_principal": z.string(),
  "proposer_name": z.string().optional(),
  "approver_admin_id": z.string().uuid().optional(),
  "approver_principal": z.string().optional(),
  "approver_name": z.string().optional(),
  "proposed_at": z.string().datetime({ offset: true }),
  "approved_at": z.string().datetime({ offset: true }).optional(),
  "expires_at": z.string().datetime({ offset: true }).optional()
}).strict();

export const budgetRaisesListSchema = z.object({
  "budget_raises": z.array(budgetRaiseSchema)
}).strict();

export const budgetRaiseCreatedIdSchema = z.object({
  "id": z.string().uuid()
}).strict();

export const killSwitchResultSchema = z.object({
  "tenant_id": z.string().uuid(),
  "fired": z.boolean(),
  "reason": z.enum(["kill_switch"])
}).strict();

export const cnfPinRequestSchema = z.object({
  "cnf_thumbprint": z.string().regex(new RegExp("^[A-Za-z0-9_-]{43}$")),
  "cnf_method": z.enum(["mtls", "dpop"])
}).strict();

export const resetProneAckSchema = z.object({
  "acknowledged_at": z.string().datetime({ offset: true }).optional()
}).strict();
